/**
 * Venues service — public venue endpoints.
 *
 * `lookupByDomain(hostname)`: BYO-domain → venue at SPA boot; endpoint is
 * anonymous, rate-limited, 404 on any failed lookup (anti-enumeration).
 *
 * `trackConversionEvent`: anonymous conversion tracking to
 * POST /track/conversion-event. No-op when navigator.doNotTrack === '1';
 * 2s-debounced batching; flushes via sendBeacon (survives page-unload —
 * matters for directions/phone navigation-away clicks) with fetch
 * keepalive fallback + pagehide flush; swallows all errors — tracking
 * must never break the public microsite UX.
 */

import httpClient from '../utils/httpClient';

export interface VenueByDomainResult {
  venue_id: string;
  slug: string;
  name: string;
}

const venuesService = {
  /**
   * Resolve a BYO custom-domain hostname to its venue. Returns the
   * venue identity {venue_id, slug, name} when the hostname is mapped
   * to a verified + SSL-issued PartnerDomain row; otherwise throws an
   * httpClient ApiError (status 404 on miss, 400 on bad hostname, 429 on
   * rate-limit; server detail on .message).
   */
  async lookupByDomain(hostname: string): Promise<VenueByDomainResult> {
    const params = new URLSearchParams({ hostname });
    return (await httpClient.get(`/venues/by-domain?${params.toString()}`)).data;
  },
};

export default venuesService;


// ── Anonymous conversion-event tracking ──

const TRACK_ENDPOINT = '/api/track/conversion-event';
const DEBOUNCE_MS = 2000;
const MAX_BATCH = 50;

interface BufferedEvent {
  venue_id: string;
  event_type: string;
}

// Module-level buffer + timer. Shared across all callers in the SPA;
// each event is independent (venue_id is on the row, not the buffer).
const _buffer: BufferedEvent[] = [];
let _timerId: ReturnType<typeof setTimeout> | null = null;

function _isDoNotTrack(): boolean {
  if (typeof navigator === 'undefined') return false;
  const n = navigator as unknown as { doNotTrack?: string; msDoNotTrack?: string };
  return n.doNotTrack === '1' || n.msDoNotTrack === '1';
}

function _flush(): void {
  if (_buffer.length === 0) return;
  const events = _buffer.splice(0, MAX_BATCH);
  const body = JSON.stringify({ events });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon survives page unload — best fit for navigation-away
      // clicks (phone, directions, social). Fire-and-forget.
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(TRACK_ENDPOINT, blob);
      return;
    }
    // Fallback for older browsers: fetch with keepalive. No await; swallow.
    fetch(TRACK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
  } catch {
    /* tracking failure must NEVER break the UI */
  }
}

/**
 * Fire a conversion event. Returns early on DNT or bad input; batched
 * per the header notes. eventType must be one of CONVERSION_EVENT_TYPES
 * (backend/models/conversion_event.py) — unknown types are silently
 * dropped by the backend with 204 (anti-enumeration).
 */
export function trackConversionEvent(eventType: string, venueId: string): void {
  if (_isDoNotTrack()) return;
  if (!eventType || !venueId) return;
  _buffer.push({ venue_id: venueId, event_type: eventType });
  if (_timerId !== null) clearTimeout(_timerId);
  _timerId = setTimeout(() => { _timerId = null; _flush(); }, DEBOUNCE_MS);
}

// Page-unload flush — ensures events buffered in the last 2 sec don't
// drop when the user closes the tab or navigates away. sendBeacon is
// the only reliable transport during pagehide.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', _flush);
}

// Test seam — only used by vitest. Allows tests to inspect/clear state
// without exposing the buffer as a public API.
export const __TEST_ONLY__ = {
  getBufferLength: () => _buffer.length,
  reset: () => {
    _buffer.length = 0;
    if (_timerId !== null) { clearTimeout(_timerId); _timerId = null; }
  },
};
